/**
 * @file resolveDesktopKeepControl.ts
 * @description The artifact-level "keep on my computer" control's whole state
 * matrix — label, tooltip, aria label, and why each of its three affordances
 * refuses. Consumer: `DesktopKeepControl`, which owns only the write. Internal
 * to this folder; `index.ts` does not re-export it.
 *
 * A disabled reason always outranks a state sentence. The control is disabled
 * for four causes the state sentences cannot name — an artifact no pin can
 * name, no signed-in user, an unread user document, a write in flight — and a
 * tooltip that described the pin state instead would leave the reader with a
 * dead button and no explanation. Every control that carries one of these
 * sentences is wrapped in a span, because antd fires no tooltip off a disabled
 * button.
 *
 * The two menu items refuse for reasons the button does not, so each carries its
 * own sentence rather than a boolean: a disabled item with no title is the same
 * unexplained dead control the tooltips exist to prevent. `undefined` is the
 * only way an item reads as enabled.
 */

import type { DesktopPinScope } from '#types';
import { desktopKeepControlLabel } from './desktopKeepControlLabel';

export type DesktopKeepControlRequest = {
    /** Which word the copy uses; known even before the path resolves. */
    artifactLabel: 'form' | 'record';
    /** From `resolveDesktopPinPath`. `null` for an artifact no pin can name. */
    pinPath: string | null;
    signedIn: boolean;
    /** `useDesktopPin().loading`; `keep` throws while the user document is unread. */
    loading: boolean;
    /** A write of this control's is in flight. */
    pending: boolean;
    /** `useDesktopPin().isPinned`. */
    isPinned: boolean;
    /** From `useDesktopPin`. `null` when this artifact is not pinned at all. */
    scope: DesktopPinScope | null;
};

export type DesktopKeepControlState = {
    /** The main button's text, which the per-file toggle's copy names. */
    label: string;
    disabled: boolean;
    tooltip: string;
    ariaLabel: string;
    /** Why the "Keep every file" menu item refuses, or `undefined` when it does not. */
    keepEveryFileDisabledReason: string | undefined;
    /** Why the "Stop keeping" menu item refuses, or `undefined` when it does not. */
    stopKeepingDisabledReason: string | undefined;
};

export const resolveDesktopKeepControl = ({
    artifactLabel,
    pinPath,
    signedIn,
    loading,
    pending,
    isPinned,
    scope,
}: DesktopKeepControlRequest): DesktopKeepControlState => {
    const keepsEveryFile = scope?.kind === 'document';
    const keptFileCount = scope?.kind === 'files' ? scope.fileUids.length : undefined;
    const disabled = pinPath === null || !signedIn || loading || pending;

    const blockedTooltip = pinPath === null
        ? `This ${artifactLabel} cannot be kept on your computer.`
        : !signedIn
            ? 'Sign in to keep this on your computer.'
            : loading
                ? 'Reading what you keep on your computer.'
                : pending
                    ? 'Saving what you keep on your computer.'
                    : null;

    const everyFileKeptSentence = `Every file in this ${artifactLabel} is kept on your computer.`;

    const stateTooltip = !isPinned
        ? `Copy the files in this ${artifactLabel} to your computer and keep them up to date.`
        : keepsEveryFile
            ? everyFileKeptSentence
            : keptFileCount === 0
                ? `This ${artifactLabel} is kept, but none of its files are.`
                : `${keptFileCount} of this ${artifactLabel}'s files are kept on your computer.`;

    return {
        label: desktopKeepControlLabel(isPinned),
        disabled,
        tooltip: blockedTooltip ?? stateTooltip,
        ariaLabel: isPinned
            ? `Stop keeping this ${artifactLabel} on my computer`
            : `Keep this ${artifactLabel} on my computer`,
        keepEveryFileDisabledReason: blockedTooltip
            ?? (keepsEveryFile ? everyFileKeptSentence : undefined),
        stopKeepingDisabledReason: blockedTooltip
            ?? (isPinned ? undefined : `This ${artifactLabel} is not kept on your computer.`),
    };
};
